import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import {
  PayPalConnectionStatus,
  PayPalTransaction,
  PayPalTransactionSummary,
  PayPalTransactionFilters,
  PayPalBalance,
  PayPalReconciliationStatus,
} from '../../../types/paypal';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

// Sort options
export type PayPalSortField = 'transaction_date' | 'amount' | 'payer_name' | 'payer_email';
export type SortDirection = 'asc' | 'desc';

interface PayPalContextType {
  // Connection
  connectionStatus: PayPalConnectionStatus | null;
  connectionLoading: boolean;
  checkConnection: () => Promise<void>;
  
  // Balance
  balance: PayPalBalance | null;
  balanceLoading: boolean;
  fetchBalance: () => Promise<void>;
  
  // Transactions
  transactions: PayPalTransaction[];
  transactionsLoading: boolean;
  fetchTransactions: () => Promise<void>;
  
  // Summary
  summary: PayPalTransactionSummary | null;
  summaryLoading: boolean;
  fetchSummary: () => Promise<void>;
  
  // Filters
  filters: PayPalTransactionFilters;
  setFilters: (filters: PayPalTransactionFilters) => void;
  
  // Sorting
  sortField: PayPalSortField;
  sortDirection: SortDirection;
  setSorting: (field: PayPalSortField, direction: SortDirection) => void;
  
  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  
  // Transaction type filter (money in/out)
  transactionTypeFilter: 'all' | 'credit' | 'debit';
  setTransactionTypeFilter: (type: 'all' | 'credit' | 'debit') => void;
  
  // Sync
  syncing: boolean;
  syncTransactions: (options?: { fullSync?: boolean }) => Promise<void>;
  
  // Reconciliation
  matchTransaction: (transactionId: string, invoiceId: string) => Promise<void>;
  unmatchTransaction: (transactionId: string) => Promise<void>;
  ignoreTransaction: (transactionId: string) => Promise<void>;
  
  // Computed/filtered transactions
  filteredTransactions: PayPalTransaction[];
}

const PayPalContext = createContext<PayPalContextType | null>(null);

export function usePayPal() {
  const context = useContext(PayPalContext);
  if (!context) {
    throw new Error('usePayPal must be used within a PayPalProvider');
  }
  return context;
}

interface PayPalProviderProps {
  children: ReactNode;
}

export function PayPalProvider({ children }: PayPalProviderProps) {
  // State
  const [connectionStatus, setConnectionStatus] = useState<PayPalConnectionStatus | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [balance, setBalance] = useState<PayPalBalance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [transactions, setTransactions] = useState<PayPalTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [summary, setSummary] = useState<PayPalTransactionSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [filters, setFilters] = useState<PayPalTransactionFilters>({});
  const [syncing, setSyncing] = useState(false);
  
  // Sorting
  const [sortField, setSortField] = useState<PayPalSortField>('transaction_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Search & type filter
  const [searchQuery, setSearchQuery] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'all' | 'credit' | 'debit'>('all');

  // Check connection status
  const checkConnection = useCallback(async () => {
    setConnectionLoading(true);
    try {
      const resp = await authFetch(`${CF_BASE}/getPayPalConnectionStatusHttp`, { method: 'GET' });
      const status = await resp.json() as PayPalConnectionStatus;
      console.log('[PayPal] Connection status:', status);
      if (resp.ok) {
        setConnectionStatus(status);
      } else {
        console.error('[PayPal] Connection check failed:', status);
        setConnectionStatus({ connected: false });
      }
    } catch (err) {
      console.error('Failed to check PayPal connection:', err);
      setConnectionStatus({ connected: false });
    } finally {
      setConnectionLoading(false);
    }
  }, []);

  // Fetch balance
  const fetchBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const resp = await authFetch(`${CF_BASE}/getPayPalBalanceHttp`, { method: 'GET' });
      const data = await resp.json();
      console.log('[PayPal] Balance response:', data);
      if (resp.ok) {
        setBalance(data);
      } else {
        console.error('[PayPal] Balance error:', data);
      }
    } catch (err) {
      console.error('Failed to fetch PayPal balance:', err);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.reconciliationStatus) params.set('reconciliationStatus', filters.reconciliationStatus);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.limit) params.set('limit', filters.limit.toString());
      
      const url = `${CF_BASE}/getPayPalTransactionsHttp${params.toString() ? '?' + params.toString() : ''}`;
      const resp = await authFetch(url, { method: 'GET' });
      if (resp.ok) {
        const data = await resp.json();
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error('Failed to fetch PayPal transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  }, [filters]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const resp = await authFetch(`${CF_BASE}/getPayPalTransactionSummaryHttp`, { method: 'GET' });
      if (resp.ok) {
        const data = await resp.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch PayPal summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // Sync transactions
  const syncTransactions = useCallback(async (options?: { fullSync?: boolean }) => {
    setSyncing(true);
    console.log('[PayPal] Starting sync...', options);
    try {
      const resp = await authFetch(`${CF_BASE}/syncPayPalTransactionsHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options || {}),
      });
      const data = await resp.json();
      console.log('[PayPal] Sync response:', data);
      if (resp.ok) {
        // Refresh data after sync (including connection status for updated lastSyncAt)
        await Promise.all([fetchTransactions(), fetchSummary(), fetchBalance(), checkConnection()]);
      } else {
        console.error('[PayPal] Sync failed:', data);
      }
    } catch (err) {
      console.error('Failed to sync PayPal transactions:', err);
    } finally {
      setSyncing(false);
    }
  }, [fetchTransactions, fetchSummary, fetchBalance, checkConnection]);

  // Match transaction
  const matchTransaction = useCallback(async (transactionId: string, invoiceId: string) => {
    const resp = await authFetch(`${CF_BASE}/matchPayPalTransactionHttp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId, invoiceId }),
    });
    if (resp.ok) {
      await fetchTransactions();
      await fetchSummary();
    }
  }, [fetchTransactions, fetchSummary]);

  // Unmatch transaction
  const unmatchTransaction = useCallback(async (transactionId: string) => {
    const resp = await authFetch(`${CF_BASE}/unmatchPayPalTransactionHttp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId }),
    });
    if (resp.ok) {
      await fetchTransactions();
      await fetchSummary();
    }
  }, [fetchTransactions, fetchSummary]);

  // Ignore transaction
  const ignoreTransaction = useCallback(async (transactionId: string) => {
    const resp = await authFetch(`${CF_BASE}/ignorePayPalTransactionHttp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId }),
    });
    if (resp.ok) {
      await fetchTransactions();
      await fetchSummary();
    }
  }, [fetchTransactions, fetchSummary]);

  // Set sorting
  const setSorting = useCallback((field: PayPalSortField, direction: SortDirection) => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  // Initial load
  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Load data when connected
  useEffect(() => {
    if (connectionStatus?.connected) {
      fetchTransactions();
      fetchSummary();
      fetchBalance();
    }
  }, [connectionStatus?.connected, fetchTransactions, fetchSummary, fetchBalance]);

  // Computed: filtered and sorted transactions
  const filteredTransactions = React.useMemo(() => {
    let result = [...transactions];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(txn => 
        txn.payer_name?.toLowerCase().includes(query) ||
        txn.payer_email?.toLowerCase().includes(query) ||
        txn.transaction_id?.toLowerCase().includes(query) ||
        txn.transaction_subject?.toLowerCase().includes(query) ||
        txn.transaction_note?.toLowerCase().includes(query) ||
        txn.invoice_id?.toLowerCase().includes(query)
      );
    }
    
    // Apply transaction type filter (money in/out)
    if (transactionTypeFilter === 'credit') {
      result = result.filter(txn => txn.amount > 0);
    } else if (transactionTypeFilter === 'debit') {
      result = result.filter(txn => txn.amount < 0);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'transaction_date':
          comparison = new Date(a.transaction_date || 0).getTime() - new Date(b.transaction_date || 0).getTime();
          break;
        case 'amount':
          comparison = (a.amount || 0) - (b.amount || 0);
          break;
        case 'payer_name':
          comparison = (a.payer_name || '').localeCompare(b.payer_name || '');
          break;
        case 'payer_email':
          comparison = (a.payer_email || '').localeCompare(b.payer_email || '');
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [transactions, searchQuery, transactionTypeFilter, sortField, sortDirection]);

  const value: PayPalContextType = {
    connectionStatus,
    connectionLoading,
    checkConnection,
    balance,
    balanceLoading,
    fetchBalance,
    transactions,
    transactionsLoading,
    fetchTransactions,
    summary,
    summaryLoading,
    fetchSummary,
    filters,
    setFilters,
    sortField,
    sortDirection,
    setSorting,
    searchQuery,
    setSearchQuery,
    transactionTypeFilter,
    setTransactionTypeFilter,
    syncing,
    syncTransactions,
    matchTransaction,
    unmatchTransaction,
    ignoreTransaction,
    filteredTransactions,
  };

  return (
    <PayPalContext.Provider value={value}>
      {children}
    </PayPalContext.Provider>
  );
}
